<template>
  <div class="main-out">
    <!-- Oberer Header mit Namen -->
    <header class="header">
      <div v-for="worker in workers" :key="worker.id" class="worker">
        {{ worker.firstName }} {{ worker.lastName }}
      </div>
    </header>

    <!-- Aufträge Bereich -->
    <div class="aufträge">
      <div
        v-for="worker in workers"
        :key="worker.id"
        class="out-auftrag"
        @dragover.prevent
        @drop="onDrop(worker.username)"
      >
        <!-- Filtere Aufträge basierend auf dem Mitarbeiter -->
        <div
          v-for="(auftrag, index) in getAufträgeByWorker(worker.username)"
          :key="auftrag.id"
          class="auftrag"
          :class="{ dragging: draggedAuftrag && draggedAuftrag.id === auftrag.id }"
          draggable="true"
          @dragstart="onDragStart($event, auftrag, index)"
          @dragover.prevent
          @drop="onDropWithinColumn(worker.username, index)"
        >
          <div class="auftrag-header">{{ auftrag.title }} - {{ auftrag.customer }}</div>
          <div class="auftrag-details">{{ auftrag.address }}</div>
          <p>um: {{ auftrag.time }}</p>
        </div>
      </div>
    </div>

    <!-- Bereich für ungelistete Aufträge -->
    <div class="ungelistete-aufträge">
      <h2>Ungelistete Aufträge</h2>
      <div
        v-for="auftrag in getUnassignedAufträge()"
        :key="auftrag.id"
        class="auftrag"
        draggable="true"
        @dragstart="onDragStart($event, auftrag, index)"
        @dragover.prevent
        @drop="onDropWithinColumn(worker.FIRST_NAME, index)"
      >
        <div class="auftrag-header">{{ auftrag.title }} - {{ auftrag.customer }}</div>
        <div class="auftrag-details"> Adresse:{{ auftrag.address }}</div>
        <p>Datum: {{ formatDate(auftrag.date) }} um {{ auftrag.time }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { keycloak, initKeycloak } from '../keycloak';

axios.defaults.withCredentials = true;

export default {
  setup() {
    const aufträge = ref([]);
    const workers = ref([]);
    const draggedAuftrag = ref(null);
    const draggedAuftragIndex = ref(null);
    const draggedFromWorker = ref(null);

    const editAuftrag = ref({
      id: null,
      title: '',
      address: '',
      customer: '',
      duration: '',
      time: '',
      email: '',
      phone: '',
      date: '',
    });

    axios.interceptors.request.use(config => {
      config.headers['Authorization'] = `Bearer ${keycloak.token}`;
      return config;
    }, error => {
      return Promise.reject(error);
    });

    const fetchAufträge = async () => {
      try {
        const response = await axios.post(`/api/alleauftaege`);
        aufträge.value = response.data;
        console.log('Fetched Aufträge:', aufträge.value); // Log die geladenen Aufträge
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchWorkers = async () => {
      try {
        const response = await axios.get(`/api/worker`);
        workers.value = response.data;
        console.log('Fetched Workers:', workers.value); // Log die geladenen Worker
      } catch (error) {
        console.error('Error fetching workers:', error);
      }
    };

    const getAufträgeByWorker = (workerFirstName) => {
      const tasks = aufträge.value
        .filter((auftrag) => auftrag.worker === workerFirstName)
        .sort((a, b) => a.order - b.order); // Sortierung nach der `order`-Spalte
      console.log(`Tasks for ${workerFirstName}:`, tasks); // Log die Aufträge für den Mitarbeiter
      return tasks;
    };

    const getUnassignedAufträge = () => {
      const unassigned = aufträge.value
        .filter((auftrag) => !auftrag.worker)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      console.log('Unassigned Aufträge:', unassigned); // Log die ungelisteten Aufträge
      return unassigned;
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const onDragStart = (event, auftrag, index) => {
      draggedAuftrag.value = auftrag;
      draggedAuftragIndex.value = index;
      draggedFromWorker.value = auftrag.worker;
      event.dataTransfer.effectAllowed = 'move';
      console.log(`Drag started for Auftrag: ${auftrag.title}, Index: ${index}, From Worker: ${auftrag.worker}`); // Log beim Drag-Start

    };


    const onDragEnd = () => {
      console.log('Drag ended'); // Log beim Drag-Ende
      draggedAuftrag.value = null;
      draggedAuftragIndex.value = null;
      draggedFromWorker.value = null;
    };

    const onDropWithinColumn = (workerFirstName, index) => {
      console.log(`Dropped within column for: ${workerFirstName}, Index: ${index}`); // Log beim Drop innerhalb der Spalte
      if (draggedAuftrag.value && draggedFromWorker.value === workerFirstName) {
        const tasks = getAufträgeByWorker(workerFirstName);
        const draggedTaskIndex = tasks.findIndex((task) => task.id === draggedAuftrag.value.id);
        if (draggedTaskIndex !== -1) {
          const [removed] = tasks.splice(draggedTaskIndex, 1);
          tasks.splice(index, 0, removed);
          console.log(`Moved Auftrag: ${removed.title} to Index: ${index}`); // Log das Verschieben des Auftrags

          tasks.forEach((task, idx) => {
            task.order = idx;
          });

          aufträge.value = aufträge.value.map((auftrag) => {
            const updatedTask = tasks.find((task) => task.id === auftrag.id);
            return updatedTask ? { ...auftrag, order: updatedTask.order } : auftrag;
          });

          axios
            .post(`/api/auftraege/reorder`, tasks.map((task) => ({
              id: task.id,
              order: task.order,
            })))
            .catch((error) => console.error('Error updating order on server:', error));
        }
      }
    };

    const onDrop = async (workerFirstName) => {
      console.log(`Dropped Auftrag for Worker: ${workerFirstName}`); // Log beim Drop
      if (draggedAuftrag.value) {
        const updatedDate = new Date(draggedAuftrag.value.date).toISOString().split('T')[0];

        const updatedAuftrag = { 
          ...draggedAuftrag.value, 
          worker: workerFirstName,
          date: updatedDate  
        };

        const previousIndex = aufträge.value.findIndex((a) => a.id === draggedAuftrag.value.id);
        if (previousIndex !== -1) {
          aufträge.value.splice(previousIndex, 1);
          console.log(`Removed Auftrag from previous index: ${previousIndex}`); // Log das Entfernen des Auftrags
        }

        aufträge.value.push(updatedAuftrag);
        console.log('Updated Aufträge after drop:', aufträge.value); // Log die aktualisierten Aufträge
        try {
          await axios.post(`/api/auftraege/update`, updatedAuftrag);
        } catch (error) {
          console.error('Error updating worker:', error);
          aufträge.value.splice(previousIndex, 0, draggedAuftrag.value);
        }

        draggedAuftrag.value = null;
        draggedAuftragIndex.value = null;
        draggedFromWorker.value = null;
      }
    };

    onMounted(() => {
      fetchAufträge();
      fetchWorkers();
    });

    return {
      aufträge,
      workers,
      getAufträgeByWorker,
      getUnassignedAufträge,
      formatDate,
      onDragStart,
      onDragEnd,
      onDropWithinColumn,
      onDrop,
    };
  },
};
</script>

<style scoped>
.main-out {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100vh;
  background-color: #111827;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-around;
  padding: 10px;
  border-bottom: 1px solid #374151;
  gap: 10px;
}

.worker {
  flex: 1;
  text-align: center;
  padding: 10px;
  color: #D1D5DB;
  background-color: #1F2937;
  border-radius: 8px;
}

.aufträge {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  min-height: 100px;
  min-width: 100%;
}

.out-auftrag {
  flex: 1;
  margin: 10px;
}

.auftrag {
  margin-bottom: 10px;
  background-color: #1F2937;
  border-radius: 8px;
  padding: 15px;
  color: #D1D5DB;
}

.auftrag-header {
  font-size: 1.2rem;
  font-weight: bold;
}

.auftrag-details {
  font-size: 1rem;
}

.auftrag:hover {
  background-color: #374151;
}

.auftrag.deleted {
  background-color: #DC2626;
}

.auftrag.completed {
  background-color: #10B981;
}

.ungelistete-aufträge {
  width: 100%;
  margin-top: 20px;
  padding: 10px;
  background-color: #1F2937;
  color: #D1D5DB;
  border-radius: 8px;
}

.ungelistete-aufträge h2 {
  margin-bottom: 10px;
  text-align: center;
}
</style>